import { Trans } from "react-i18next";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { usePerspectiveState } from "@shared/context/PerspectiveStateContext";
import FeatureErrorBoundary from "@shared/components/FeatureErrorBoundary";
import EEGPlayableShell from "../components/EEGPlayableShell";
import {
  markEegStartup,
  resetEegStartupTelemetry,
} from "../utils/eegStartupTelemetry";
import "./EEGViewerPage.css";

const EEGViewer = lazy(async () => {
  markEegStartup("viewerChunkRequested");
  const module = await import("../components/EEGViewer");
  markEegStartup("viewerChunkLoaded");
  return module;
});

const EEGViewerPage: React.FC = () => {
  const { studyId: routeStudyId } = useParams<{ studyId: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const patientId = searchParams.get("patientId") || "";
  const legacyStudyId = searchParams.get("loadStudy") || "";
  const studyId = legacyStudyId || routeStudyId || "";
  const recordingId =
    searchParams.get("recordingId") || (legacyStudyId ? routeStudyId : undefined);
  const mode = searchParams.get("mode");

  const { setClinicalMode } = usePerspectiveState();
  const [loadViewer, setLoadViewer] = useState(false);

  useEffect(() => {
    resetEegStartupTelemetry();
  }, [studyId, recordingId]);

  useEffect(() => {
    if (mode === "clinical") {
      setClinicalMode(true);
    }
  }, [mode, setClinicalMode]);

  useEffect(() => {
    document.body.classList.add("eeg-viewer-active");
    document.documentElement.classList.add("eeg-viewer-active");

    return () => {
      document.body.classList.remove("eeg-viewer-active");
      document.documentElement.classList.remove("eeg-viewer-active");
    };
  }, []);

  useEffect(() => {
    const requestIdleCallbackId =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(() => setLoadViewer(true), {
            timeout: 120,
          })
        : null;
    const timeoutId =
      requestIdleCallbackId === null
        ? window.setTimeout(() => setLoadViewer(true), 16)
        : null;

    return () => {
      if (requestIdleCallbackId !== null) {
        window.cancelIdleCallback(requestIdleCallbackId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  if (!studyId) {
    return (
      <FeatureErrorBoundary featureName="EEG Viewer">
        <div><Trans ns="features" i18nKey="ui.copy.33df6b4a14a9" /></div>
      </FeatureErrorBoundary>
    );
  }

  return (
    <FeatureErrorBoundary featureName="EEG Viewer">
      {loadViewer ? (
        <Suspense fallback={<EEGPlayableShell />}>
          <EEGViewer
            studyId={studyId}
            patientId={patientId}
            recordingId={recordingId}
          />
        </Suspense>
      ) : (
        <EEGPlayableShell />
      )}
    </FeatureErrorBoundary>
  );
};

export default EEGViewerPage;
